import { Flex, Typography } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { Card } from '../../Card';
import { Heading } from '../../Heading';
import { BookOpen } from 'lucide-react';
import { BasicProduct } from '@/types';
import { useTranslation } from 'react-i18next';
import ProductInputsRow from './ProductInputsRow';
import ProductCatalogModal from './ProductCatalogModal';
import useProductStore from '@/stores/productStore';

const { Text } = Typography;

interface ProductDetailsCardProps {
  products: BasicProduct[];
  handleCreateProduct: () => void;
  handleDeleteProduct: (index: number) => void;
  handleUpdateProduct: (index: number, field: keyof BasicProduct, value: string) => void;
  handleUpdateProductBatch?: (index: number, updates: Partial<Record<keyof BasicProduct, string>>) => void;
  handleAddProducts?: (products: BasicProduct[]) => void;
}

const ProductDetailsCard: React.FC<ProductDetailsCardProps> = React.memo(
  ({ products, handleCreateProduct, handleDeleteProduct, handleUpdateProduct, handleUpdateProductBatch, handleAddProducts }) => {
    const { isProductListValid, submitAttempted } = useProductStore();
    const { t } = useTranslation();

    const [catalogModalOpen, setCatalogModalOpen] = useState(false);
    const [touchedFields, setTouchedFields] = useState<Record<number, Set<string>>>({});

    // Multi-row mode: used by bulk import edit (no catalog, multiple product rows)
    const isMultiRowMode = !handleUpdateProductBatch;

    // Single-row mode: the order always has exactly ONE product row (index 0)
    const product = products[0] ?? { title: '', price: '', quantity: '1' };

    const hasValidProduct = useMemo(() => {
      return !!product.title && (!!product.price || product.price === 0);
    }, [product]);

    const handleFieldBlur = useCallback((field: keyof BasicProduct) => {
      setTouchedFields((prev) => {
        const current = prev[0] ?? new Set<string>();
        if (current.has(field)) return prev;
        const updated = new Set(current);
        updated.add(field);
        return { ...prev, 0: updated };
      });
    }, []);

    const handleUpdateProductCallback = useCallback(
      (field: keyof BasicProduct, value: string) => {
        handleUpdateProduct(0, field, value);
      },
      [handleUpdateProduct],
    );

    // When catalog products are selected, merge into the single product row:
    // - Title = comma-separated product names (appended to existing)
    // - Price = sum of all prices (added to existing)
    // - Fragile = true if any product is fragile
    const handleCatalogSelect = useCallback(
      (selectedProducts: BasicProduct[]) => {
        if (!handleUpdateProductBatch) return;

        const currentTitle = product.title || '';
        const currentPrice = parseFloat(String(product.price)) || 0;
        const currentFragile = product.is_fragile || false;

        // Build new title by appending selected product names
        const newNames = selectedProducts.map((p) => p.title).filter(Boolean);
        const combinedTitle = currentTitle
          ? [currentTitle, ...newNames].join(', ')
          : newNames.join(', ');

        // Sum only products that have a meaningful price (skip null/empty/zero)
        const productsWithPrice = selectedProducts.filter(
          (p) => p.price !== null && p.price !== undefined && String(p.price) !== '' && Number(p.price) > 0,
        );
        const addedPrice = productsWithPrice.reduce((sum, p) => sum + (parseFloat(String(p.price)) || 0), 0);

        // Only update price if there's something to add (existing price or new prices)
        const hasExistingPrice = currentPrice > 0;
        const hasNewPrices = productsWithPrice.length > 0;
        const combinedPrice = hasExistingPrice || hasNewPrices ? currentPrice + addedPrice : '';

        // Fragile if any selected product is fragile
        const anyFragile = currentFragile || selectedProducts.some((p) => p.is_fragile);

        handleUpdateProductBatch(0, {
          title: combinedTitle,
          price: String(combinedPrice),
          is_fragile: anyFragile ? 'true' : 'false',
        });
      },
      [product, handleUpdateProductBatch],
    );

    // Compute errors for the single product row
    const rowErrors = useMemo(() => {
      const touched = touchedFields[0];
      const shouldShowField = (field: string) => submitAttempted || touched?.has(field);

      const errors: { title?: boolean; price?: boolean } = {};

      if (submitAttempted || (product.title || product.price)) {
        if (!product.title && shouldShowField('title')) {
          errors.title = true;
        }
        if (!product.price && product.price !== 0 && shouldShowField('price') && submitAttempted) {
          errors.price = true;
        }
      }

      return errors.title || errors.price ? errors : undefined;
    }, [product, touchedFields, submitAttempted]);

    // ── Multi-row mode (bulk import edit) ──
    if (isMultiRowMode) {
      return (
        <Card>
          <div className='flex items-center justify-between mb-4'>
            <div className='flex items-center'>
              <Heading title={t('product_details')} fontSize='text-xs' fontWeight='font-bold' color='text-sd-muted-foreground' extraClassName='uppercase tracking-wider' />
              <Text className='text-colorError text-sm mx-1'>*</Text>
            </div>
          </div>
          {products.map((p, index) => (
            <div key={index} className='mb-3'>
              <ProductInputsRow
                product={p}
                handleChangeProductKey={(field, value) => handleUpdateProduct(index, field, value)}
                handleSubmit={handleCreateProduct}
                showDeleteButton={products.length > 1}
                onDelete={() => handleDeleteProduct(index)}
                hideButton={index < products.length - 1}
                showQuantity
                disableCatalog
              />
            </div>
          ))}
        </Card>
      );
    }

    // ── Single-row mode (add/edit order with catalog) ──
    return (
      <Card>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center'>
            <Heading title={t('product_details')} fontSize='text-xs' fontWeight='font-bold' color='text-sd-muted-foreground' extraClassName='uppercase tracking-wider' />
            <Text className='text-colorError text-sm mx-1'>*</Text>
          </div>
          <button
            type='button'
            onClick={() => setCatalogModalOpen(true)}
            className='h-7 px-3 text-xs font-medium gap-1 flex items-center rounded-md border border-sd-input bg-sd-background text-sd-foreground hover:bg-sd-accent hover:text-sd-accent-foreground transition-colors cursor-pointer'
          >
            <BookOpen className='h-3 w-3' />
            {String(t('catalog', { defaultValue: 'Catalog' }))}
          </button>
        </div>
        {!isProductListValid && !hasValidProduct && submitAttempted && (
          <Flex className='mb-2'>
            <Heading
              title={t('please_add_at_least_one_product')}
              color='text-colorError'
              fontSize='text-lg'
              fontWeight='font-normal'
            />
          </Flex>
        )}
        <ProductInputsRow
          product={product}
          handleChangeProductKey={handleUpdateProductCallback}
          handleChangeProductKeyBatch={handleUpdateProductBatch ? (updates) => handleUpdateProductBatch(0, updates) : undefined}
          handleSubmit={() => {}}
          hideButton
          errors={rowErrors}
          onFieldBlur={handleFieldBlur}
        />
        <ProductCatalogModal
          visible={catalogModalOpen}
          onClose={() => setCatalogModalOpen(false)}
          onSelect={handleCatalogSelect}
        />
      </Card>
    );
  },
);

ProductDetailsCard.displayName = 'ProductDetailsCard';

export default ProductDetailsCard;
